import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, apiPut, apiPost, apiPatch, apiDelete } from '../utils/api';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ResponsiveLogo } from '../components/ThemeLogo';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Sun, Moon, ChevronRight, Bot, Brain, Users,
  Target, Shield, Lightbulb, Eye, Mic, FileText, Zap,
  Settings, Key, BarChart3, CheckCircle2, XCircle,
  Loader2, Lock, Unlock, Activity, Plus, Pencil, Trash2,
  Crown, AlertTriangle, ChevronDown, ChevronUp, Globe,
} from 'lucide-react';

const MODULE_ICONS = {
  'MrCanvas': Target,
  'Advisory Board': Users,
  'Sharpen Focus': Brain,
  'Company Intelligence Engine': Lightbulb,
};

const AGENT_ICONS = {
  'Question Master': Target,
  'Brutal Editor': Shield,
  'Diagnosis Engine': BarChart3,
  'Workspace Generator': Zap,
  'Board Matchmaker': Users,
  'Board Moderator': Users,
  'Executive Report Writer': FileText,
  'Business Investigator': Eye,
  "Devil's Advocate": Shield,
  'Strategic Analyst': Brain,
  'Chief Risk Officer': Shield,
  'CEO Decision Coach': Target,
  'Business Intelligence Analyst': Lightbulb,
  'Document Analyst': FileText,
  'Voice Transcriber': Mic,
  'Description Analyst': FileText,
};

function StatsCard({ icon: Icon, label, value, color }) {
  return (
    <div className="p-4 rounded-xl border border-border bg-card">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

function AgentModule({ module }) {
  const ModuleIcon = MODULE_ICONS[module.module] || Bot;
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`module-${module.module.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="p-4 border-b border-border bg-muted/30 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-odapto-orange/10 flex items-center justify-center shrink-0">
          <ModuleIcon className="w-5 h-5 text-odapto-orange" />
        </div>
        <div>
          <h3 className="font-bold text-sm">{module.module}</h3>
          <p className="text-xs text-muted-foreground">{module.description}</p>
        </div>
        <span className="ml-auto text-xs font-medium bg-odapto-orange/10 text-odapto-orange px-2 py-0.5 rounded-full">
          {module.agents.length} agents
        </span>
      </div>
      <div className="divide-y divide-border">
        {module.agents.map((agent, i) => {
          const AgentIcon = AGENT_ICONS[agent.name] || Bot;
          return (
            <div key={i} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/20 transition-colors">
              <AgentIcon className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{agent.name}</p>
                <p className="text-xs text-muted-foreground truncate">{agent.role}</p>
              </div>
              <span className="text-[10px] font-mono bg-muted px-2 py-0.5 rounded">{agent.model}</span>
              <span className={`w-2 h-2 rounded-full shrink-0 ${agent.status === 'active' ? 'bg-emerald-500' : 'bg-zinc-400'}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AdvisoryLibrary({ advisors }) {
  if (!advisors || advisors.length === 0) return null;
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="advisory-library">
      <div className="p-4 border-b border-border bg-muted/30 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center shrink-0">
          <Users className="w-5 h-5 text-violet-500" />
        </div>
        <div>
          <h3 className="font-bold text-sm">Advisory Board Library</h3>
          <p className="text-xs text-muted-foreground">{advisors.length} world-class advisor personas available</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
        {advisors.map(advisor => (
          <div key={advisor.id} className="bg-card p-3 hover:bg-muted/20 transition-colors">
            <p className="font-medium text-sm">{advisor.name}</p>
            <p className="text-xs text-odapto-orange">{advisor.title}</p>
            <p className="text-[10px] text-muted-foreground mt-1">{advisor.expertise}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function AIKeySettings({ settings, onSave }) {
  const [customKey, setCustomKey] = useState('');
  const [saving, setSaving] = useState(false);
  const isCustom = settings?.key_source === 'custom';

  const handleSaveCustom = async () => {
    if (!customKey.trim().startsWith('sk-')) {
      toast.error('Invalid key format. Must start with "sk-"');
      return;
    }
    setSaving(true);
    try {
      const res = await apiPut('/admin/ai/settings', { custom_openai_key: customKey.trim(), use_custom_key: true });
      if (res.ok) {
        toast.success('Custom API key saved');
        setCustomKey('');
        onSave();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save');
      }
    } catch { toast.error('Connection error'); }
    setSaving(false);
  };

  const handleRevertPlatform = async () => {
    setSaving(true);
    try {
      const res = await apiPut('/admin/ai/settings', { use_custom_key: false });
      if (res.ok) { toast.success('Reverted to platform key'); onSave(); }
    } catch { toast.error('Connection error'); }
    setSaving(false);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="ai-key-settings">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
          <Key className="w-5 h-5 text-amber-500" />
        </div>
        <div>
          <h3 className="font-bold text-sm">API Key Configuration</h3>
          <p className="text-xs text-muted-foreground">Configure which OpenAI API key powers your AI agents</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isCustom ? 'bg-muted' : 'bg-emerald-500/10'}`}>
            {isCustom ? <Lock className="w-4 h-4 text-muted-foreground" /> : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">Platform Default Key</p>
            <p className="text-xs text-muted-foreground">Pre-configured OpenAI access — no setup required</p>
          </div>
          {!isCustom && <span className="text-xs font-medium text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full">Active</span>}
          {isCustom && (
            <Button variant="outline" size="sm" onClick={handleRevertPlatform} disabled={saving} className="text-xs">
              Use This
            </Button>
          )}
        </div>

        <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isCustom ? 'bg-emerald-500/10' : 'bg-muted'}`}>
            {isCustom ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Unlock className="w-4 h-4 text-muted-foreground" />}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium">Custom OpenAI Key</p>
            {isCustom ? (
              <p className="text-xs text-muted-foreground">Using: {settings?.custom_key_preview}</p>
            ) : (
              <p className="text-xs text-muted-foreground">Use your own OpenAI API key — billed to your account</p>
            )}
          </div>
          {isCustom && <span className="text-xs font-medium text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full">Active</span>}
        </div>

        {!isCustom && (
          <div className="flex gap-2">
            <Input
              type="password"
              value={customKey}
              onChange={e => setCustomKey(e.target.value)}
              placeholder="sk-..."
              className="flex-1 font-mono text-sm"
              data-testid="custom-key-input"
            />
            <Button onClick={handleSaveCustom} disabled={saving || !customKey.trim()} className="bg-odapto-orange hover:bg-odapto-orange-hover text-white" data-testid="save-key-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Key'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════
// ADVISOR MANAGEMENT
// ═══════════════════════════════════════

const LENS_LABELS = {
  chairman: 'Chairman', strategist: 'Strategist', entrepreneur: 'Entrepreneur',
  investor: 'Investor', systems_thinker: 'Systems Thinker', organization_builder: 'Org Builder',
};
const STATUS_STYLES = {
  active: 'bg-emerald-500/10 text-emerald-600', inactive: 'bg-zinc-500/10 text-zinc-500',
  candidate: 'bg-amber-500/10 text-amber-600', pending: 'bg-yellow-500/10 text-yellow-700',
  approved: 'bg-emerald-500/10 text-emerald-600',
};

function AdvisorCard({ advisor, onEdit, onDelete, onApprove, onReject }) {
  const [expanded, setExpanded] = useState(false);
  const isHoy = advisor.protected;
  const hasPending = !!advisor.pending_changes;
  const isPending = advisor.status === 'pending';
  return (
    <div className={`rounded-xl border overflow-hidden ${isHoy ? 'border-amber-400/50 bg-amber-50/30 dark:bg-amber-950/10' : hasPending ? 'border-yellow-400/50 bg-yellow-50/20 dark:bg-yellow-950/10' : 'border-border bg-card'}`} data-testid={`advisor-${advisor.advisor_id}`}>
      <div className="p-4 flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isHoy ? 'bg-amber-500/15' : 'bg-muted'}`}>
          {isHoy ? <Crown className="w-5 h-5 text-amber-500" /> : <Users className="w-5 h-5 text-muted-foreground" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-bold text-sm">{advisor.name}</h4>
            {isHoy && <span className="text-[10px] font-bold bg-amber-500/20 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded">PROTECTED</span>}
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${STATUS_STYLES[advisor.status] || STATUS_STYLES.candidate}`}>
              {advisor.status?.toUpperCase()}
            </span>
            {hasPending && <span className="text-[10px] font-bold bg-yellow-500/20 text-yellow-700 px-1.5 py-0.5 rounded">PENDING CHANGES</span>}
            {advisor.council_lens && (
              <span className="text-[10px] bg-blue-500/10 text-blue-600 px-1.5 py-0.5 rounded">{LENS_LABELS[advisor.council_lens] || advisor.council_lens}</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{advisor.role}</p>
          {advisor.expertise?.length > 0 && (
            <p className="text-[11px] text-muted-foreground mt-1 line-clamp-1">{advisor.expertise.join(', ')}</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {(isPending || hasPending) && (
            <>
              <button onClick={() => onApprove(advisor)} className="p-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 transition-colors" title="Approve" data-testid={`approve-${advisor.advisor_id}`}>
                <CheckCircle2 className="w-4 h-4" />
              </button>
              <button onClick={() => onReject(advisor)} className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-500 transition-colors" title="Reject" data-testid={`reject-${advisor.advisor_id}`}>
                <XCircle className="w-4 h-4" />
              </button>
            </>
          )}
          <button onClick={() => setExpanded(e => !e)} className="p-1.5 rounded-lg hover:bg-muted transition-colors" data-testid={`expand-${advisor.advisor_id}`}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {!isHoy && <button onClick={() => onEdit(advisor)} className="p-1.5 rounded-lg hover:bg-muted transition-colors"><Pencil className="w-3.5 h-3.5" /></button>}
          {!isHoy && (
            <button onClick={() => onDelete(advisor)} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 border-t border-border/50 pt-3 space-y-2">
          {advisor.description && <div><p className="text-[10px] font-medium text-muted-foreground uppercase">Description</p><p className="text-xs">{advisor.description}</p></div>}
          {advisor.beliefs?.length > 0 && (
            <div><p className="text-[10px] font-medium text-muted-foreground uppercase">Beliefs ({advisor.beliefs.length})</p>
              <div className="space-y-1 mt-1 max-h-40 overflow-y-auto">
                {advisor.beliefs.map((b, i) => <p key={i} className="text-[11px]"><span className="font-medium">{b.id || i+1}.</span> {b.title || b}</p>)}
              </div>
            </div>
          )}
          {advisor.decision_rules?.length > 0 && (
            <div><p className="text-[10px] font-medium text-muted-foreground uppercase">Decision Algorithm ({advisor.decision_rules.length} steps)</p>
              <div className="space-y-0.5 mt-1">
                {advisor.decision_rules.map((r, i) => <p key={i} className="text-[11px]"><span className="font-medium">{r.step || i+1}.</span> {r.question}</p>)}
              </div>
            </div>
          )}
          {advisor.evidence_notes && <div><p className="text-[10px] font-medium text-muted-foreground uppercase">Evidence</p><p className="text-[11px]">{advisor.evidence_notes}</p></div>}
          {hasPending && (
            <div className="mt-2 p-2 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800">
              <p className="text-[10px] font-bold text-yellow-700 uppercase mb-1">Pending Changes (awaiting approval)</p>
              {Object.entries(advisor.pending_changes).filter(([k]) => k !== 'submitted_at').map(([k, v]) => (
                <p key={k} className="text-[11px]"><span className="font-medium">{k}:</span> {typeof v === 'string' ? v.slice(0, 100) : JSON.stringify(v).slice(0, 100)}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CouncilOfFive({ lenses }) {
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="council-of-five">
      <div className="p-4 border-b border-border bg-muted/30 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center"><Target className="w-5 h-5 text-blue-500" /></div>
        <div><h3 className="font-bold text-sm">Council of Five — Strategic Lenses</h3><p className="text-xs text-muted-foreground">Five complementary perspectives for strategic decisions</p></div>
      </div>
      <div className="divide-y divide-border">
        {lenses?.map(l => (
          <div key={l.lens_id} className="px-4 py-3 flex items-center gap-3">
            <span className="text-xs font-bold bg-blue-500/10 text-blue-600 px-2 py-0.5 rounded w-36 text-center shrink-0">{l.name}</span>
            <p className="text-xs text-muted-foreground italic">"{l.question}"</p>
          </div>
        ))}
      </div>
    </div>
  );
}

const EMPTY_FORM = { name: '', role: '', description: '', expertise: '', beliefs_text: '', reasoning_approach: '', evidence_notes: '', council_lens: '', advisor_type: 'custom', status: 'active' };

function AdvisorFormDialog({ open, onOpenChange, advisor, onSave }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isEdit = !!advisor;

  useEffect(() => {
    if (advisor) {
      setForm({
        name: advisor.name || '', role: advisor.role || '', description: advisor.description || '',
        expertise: (advisor.expertise || []).join(', '), beliefs_text: (advisor.beliefs || []).map(b => b.title || b).join('\n'),
        reasoning_approach: advisor.reasoning_approach || '', evidence_notes: advisor.evidence_notes || '',
        council_lens: advisor.council_lens || '', advisor_type: advisor.advisor_type || 'custom', status: advisor.status || 'active',
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [advisor, open]);

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (form.name.trim().length < 2) { toast.error('Name must be at least 2 characters'); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(), role: form.role.trim(), description: form.description.trim(),
      expertise: form.expertise.split(',').map(s => s.trim()).filter(Boolean),
      beliefs: form.beliefs_text.split('\n').filter(Boolean).map((t, i) => ({ id: i + 1, title: t.trim(), detail: '' })),
      reasoning_approach: form.reasoning_approach.trim(), evidence_notes: form.evidence_notes.trim(),
      council_lens: form.council_lens, advisor_type: form.advisor_type, status: form.status,
    };
    try {
      const res = isEdit
        ? await apiPatch(`/admin/advisors/${advisor.advisor_id}`, payload)
        : await apiPost('/admin/advisors', payload);
      if (res.ok) { toast.success(isEdit ? 'Advisor updated' : 'Advisor created'); onSave(); onOpenChange(false); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Failed'); }
    } catch { toast.error('Connection error'); }
    setSaving(false);
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Advisor' : 'Add New Advisor'}</DialogTitle>
          <DialogDescription>Configure advisor persona, beliefs, and reasoning.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={e => set('name', e.target.value)} data-testid="advisor-name-input" /></div>
          <div><Label className="text-xs">Role / Title</Label><Input value={form.role} onChange={e => set('role', e.target.value)} /></div>
          <div><Label className="text-xs">Description</Label><textarea value={form.description} onChange={e => set('description', e.target.value)} className="w-full min-h-[60px] p-2 rounded-lg border border-border bg-background text-sm resize-none" /></div>
          <div><Label className="text-xs">Expertise (comma-separated)</Label><Input value={form.expertise} onChange={e => set('expertise', e.target.value)} placeholder="e.g., Cash flow, Strategy, Scaling" /></div>
          <div><Label className="text-xs">Beliefs / Principles (one per line)</Label><textarea value={form.beliefs_text} onChange={e => set('beliefs_text', e.target.value)} className="w-full min-h-[80px] p-2 rounded-lg border border-border bg-background text-sm resize-none" placeholder="One principle per line..." /></div>
          <div><Label className="text-xs">Reasoning Approach</Label><textarea value={form.reasoning_approach} onChange={e => set('reasoning_approach', e.target.value)} className="w-full min-h-[40px] p-2 rounded-lg border border-border bg-background text-sm resize-none" /></div>
          <div><Label className="text-xs">Evidence / Source Notes</Label><textarea value={form.evidence_notes} onChange={e => set('evidence_notes', e.target.value)} className="w-full min-h-[40px] p-2 rounded-lg border border-border bg-background text-sm resize-none" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Council Lens</Label>
              <select value={form.council_lens} onChange={e => set('council_lens', e.target.value)} className="w-full h-9 px-2 rounded-lg border border-border bg-background text-sm">
                <option value="">None</option>
                <option value="chairman">Chairman</option>
                <option value="strategist">Strategist</option>
                <option value="entrepreneur">Entrepreneur</option>
                <option value="investor">Investor</option>
                <option value="systems_thinker">Systems Thinker</option>
                <option value="organization_builder">Org Builder</option>
              </select>
            </div>
            <div><Label className="text-xs">Type</Label>
              <select value={form.advisor_type} onChange={e => set('advisor_type', e.target.value)} className="w-full h-9 px-2 rounded-lg border border-border bg-background text-sm">
                <option value="custom">Custom</option>
                <option value="internal">Internal</option>
                <option value="external">External</option>
                <option value="candidate">Candidate</option>
              </select>
            </div>
            <div><Label className="text-xs">Status</Label>
              <select value={form.status} onChange={e => set('status', e.target.value)} className="w-full h-9 px-2 rounded-lg border border-border bg-background text-sm">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="candidate">Candidate</option>
              </select>
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={saving} className="w-full bg-odapto-orange hover:bg-odapto-orange-hover text-white" data-testid="advisor-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{isEdit ? 'Save Changes' : 'Create Advisor'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AdvisorSection({ advisors, council, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editAdvisor, setEditAdvisor] = useState(null);
  const [filter, setFilter] = useState('all'); // all | active | pending | approved | candidate

  const filtered = (advisors || []).filter(a => {
    if (filter === 'active') return a.status === 'active' || a.status === 'approved';
    if (filter === 'pending') return a.status === 'pending' || !!a.pending_changes;
    if (filter === 'candidate') return a.status === 'candidate';
    return true;
  });

  const activeCount = (advisors || []).filter(a => a.status === 'active' || a.status === 'approved').length;
  const pendingCount = (advisors || []).filter(a => a.status === 'pending' || a.pending_changes).length;
  const candidateCount = (advisors || []).filter(a => a.status === 'candidate').length;

  const handleEdit = (a) => { setEditAdvisor(a); setDialogOpen(true); };
  const handleAdd = () => { setEditAdvisor(null); setDialogOpen(true); };
  const handleDelete = async (a) => {
    if (!window.confirm(`Delete advisor "${a.name}"?`)) return;
    const res = await apiDelete(`/admin/advisors/${a.advisor_id}`);
    if (res.ok) { toast.success('Advisor deleted'); onRefresh(); }
    else { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Failed'); }
  };
  const handleApprove = async (a) => {
    const res = await apiPost(`/admin/advisors/${a.advisor_id}/approve`, {});
    if (res.ok) { const d = await res.json(); toast.success(d.message || 'Approved'); onRefresh(); }
    else { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Failed'); }
  };
  const handleReject = async (a) => {
    if (!window.confirm(`Reject ${a.pending_changes ? 'pending changes for' : ''} "${a.name}"?`)) return;
    const res = await apiPost(`/admin/advisors/${a.advisor_id}/reject`, {});
    if (res.ok) { const d = await res.json(); toast.success(d.message || 'Rejected'); onRefresh(); }
    else { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Failed'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold">Advisor System</h2>
          <p className="text-xs text-muted-foreground">{activeCount} active, {pendingCount > 0 ? `${pendingCount} pending approval, ` : ''}{candidateCount} candidates</p>
        </div>
        <Button onClick={handleAdd} className="bg-odapto-orange hover:bg-odapto-orange-hover text-white" data-testid="add-advisor-btn">
          <Plus className="w-4 h-4 mr-1" /> Add New Advisor
        </Button>
      </div>

      {/* Council of Five */}
      <div className="mb-5"><CouncilOfFive lenses={council} /></div>

      {/* Filter tabs */}
      <div className="flex gap-2 mb-4">
        {[['all', 'All'], ['active', 'Active'], ['pending', `Pending${pendingCount > 0 ? ` (${pendingCount})` : ''}`], ['candidate', 'Candidates']].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${filter === v ? 'bg-odapto-orange text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>{l}</button>
        ))}
      </div>

      {/* Advisor cards */}
      <div className="space-y-3">
        {filtered.map(a => <AdvisorCard key={a.advisor_id} advisor={a} onEdit={handleEdit} onDelete={handleDelete} onApprove={handleApprove} onReject={handleReject} />)}
        {filtered.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No advisors match this filter.</p>}
      </div>

      <AdvisorFormDialog open={dialogOpen} onOpenChange={setDialogOpen} advisor={editAdvisor} onSave={onRefresh} />
    </div>
  );
}


// ═══════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════

export default function AdminAIPage() {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [agents, setAgents] = useState(null);
  const [settings, setSettings] = useState(null);
  const [usage, setUsage] = useState(null);
  const [advisorData, setAdvisorData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [agentsRes, settingsRes, usageRes, advisorRes] = await Promise.all([
      apiGet('/admin/ai/agents'),
      apiGet('/admin/ai/settings'),
      apiGet('/admin/ai/usage'),
      apiGet('/admin/advisors'),
    ]);
    if (agentsRes.ok) setAgents(await agentsRes.json());
    if (settingsRes.ok) setSettings(await settingsRes.json());
    if (usageRes.ok) setUsage(await usageRes.json());
    if (advisorRes.ok) setAdvisorData(await advisorRes.json());
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-8 h-8 animate-spin text-odapto-orange" />
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="admin-ai-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2">
              <ResponsiveLogo className="h-8 w-auto" />
            </Link>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Admin</span>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">AI Agents</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold">AI Agents & Configuration</h1>
          <p className="text-sm text-muted-foreground">View deployed agents, manage API keys, and monitor usage</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="ai-stats">
          <StatsCard icon={Bot} label="Total Agents" value={agents?.total_agents || 0} color="text-odapto-orange" />
          <StatsCard icon={Target} label="Strategy Sessions" value={usage?.strategy_sessions || 0} color="text-blue-500" />
          <StatsCard icon={Users} label="Advisory Sessions" value={usage?.advisory_sessions || 0} color="text-violet-500" />
          <StatsCard icon={Brain} label="Focus Sessions" value={usage?.focus_sessions || 0} color="text-emerald-500" />
          <StatsCard icon={Activity} label="Knowledge Bases" value={usage?.knowledge_bases || 0} color="text-amber-500" />
        </div>

        {/* API Key Settings */}
        <div className="mb-6">
          <AIKeySettings settings={settings} onSave={fetchAll} />
        </div>

        {/* Provider Info */}
        <div className="flex items-center gap-4 mb-6 p-4 rounded-xl border border-border bg-card">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Provider</span>
          </div>
          <span className="text-sm text-muted-foreground">OpenAI</span>
          <span className="text-xs bg-muted px-2 py-0.5 rounded font-mono">gpt-5.2</span>
          <span className="text-xs bg-muted px-2 py-0.5 rounded font-mono">whisper-1</span>
          <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full ${settings?.key_source === 'custom' ? 'bg-blue-500/10 text-blue-500' : 'bg-emerald-500/10 text-emerald-500'}`}>
            {settings?.key_source === 'custom' ? 'Custom Key' : 'Platform Key'}
          </span>
        </div>

        {/* Agent Modules */}
        <div className="space-y-5 mb-8">
          <h2 className="text-lg font-bold">Deployed Agents</h2>
          {(agents?.modules || []).map((mod, i) => (
            <AgentModule key={i} module={mod} />
          ))}
        </div>

        {/* Advisory Library (legacy) */}
        <div className="mb-8">
          <h2 className="text-lg font-bold mb-4">Legacy Advisor Personas</h2>
          <AdvisoryLibrary advisors={agents?.advisory_library || []} />
        </div>

        {/* Advisor System */}
        <div className="mb-8">
          <AdvisorSection advisors={advisorData?.advisors} council={advisorData?.council_of_five} onRefresh={fetchAll} />
        </div>
      </div>
    </div>
  );
}
